// ScopeShield engine — baseline + change request log + margin impact.

export interface Deliverable {
  id: string;
  title: string;
  acceptanceCriteria: string;
  estimatedHours: number;
  locked: boolean;          // baseline = locked
}

export interface ChangeRequest {
  id: string;
  raisedISO: string;
  requestor: string;
  description: string;
  rationale: string;
  estimatedHours: number;
  priceImpact: number;
  status: 'proposed' | 'approved' | 'rejected' | 'deferred';
  decisionISO?: string;
  decisionBy?: string;
}

export interface Project {
  id: string;
  name: string;
  client: string;
  baselineDate: string;
  quotedHours: number;
  quotedPrice: number;
  hourlyRate: number;
  deliverables: Deliverable[];
  changes: ChangeRequest[];
}

export function emptyProject(): Project {
  return {
    id: crypto.randomUUID(),
    name: '', client: '',
    baselineDate: new Date().toISOString().slice(0, 10),
    quotedHours: 0, quotedPrice: 0, hourlyRate: 100,
    deliverables: [], changes: [],
  };
}

export function lockBaseline(p: Project): Project {
  return { ...p, deliverables: p.deliverables.map(d => ({ ...d, locked: true })) };
}

export function approvedExtras(p: Project): { extraHours: number; extraPrice: number } {
  const approved = p.changes.filter(c => c.status === 'approved');
  return {
    extraHours: approved.reduce((s, c) => s + c.estimatedHours, 0),
    extraPrice: approved.reduce((s, c) => s + c.priceImpact, 0),
  };
}

export function scopeCreepPct(p: Project): number {
  if (p.quotedHours <= 0) return 0;
  const { extraHours } = approvedExtras(p);
  return (extraHours / p.quotedHours) * 100;
}

export function marginImpactPct(p: Project): number {
  if (p.quotedPrice <= 0) return 0;
  const { extraHours, extraPrice } = approvedExtras(p);
  const newCost = (p.quotedHours + extraHours) * 0;  // cost untracked; treat as 0 for now
  const newRev = p.quotedPrice + extraPrice;
  return newRev > 0 ? ((newRev - newCost) / newRev) * 100 : 0;
}

export function suggestedPriceImpact(hours: number, rate: number, urgency: 'low' | 'med' | 'high'): number {
  const mult = urgency === 'high' ? 1.5 : urgency === 'med' ? 1.25 : 1.0;
  return Math.round(hours * rate * mult);
}

export function exportChangeRequestMarkdown(p: Project, c: ChangeRequest): string {
  return `# Change Request — ${p.name}

**CR ID:** ${c.id.slice(0, 8)}
**Raised:** ${c.raisedISO.slice(0, 10)}
**Requestor:** ${c.requestor}
**Status:** ${c.status.toUpperCase()}

## Description
${c.description}

## Rationale
${c.rationale}

## Impact

| | Quoted | Change | New total |
|---|---|---|---|
| Hours | ${p.quotedHours} | +${c.estimatedHours} | ${p.quotedHours + c.estimatedHours} |
| Price | $${p.quotedPrice.toLocaleString()} | +$${c.priceImpact.toLocaleString()} | $${(p.quotedPrice + c.priceImpact).toLocaleString()} |

## Approval

By: ${c.decisionBy || '_(awaiting)_'}
Date: ${c.decisionISO?.slice(0, 10) || '_(awaiting)_'}

---

_${p.client} · Generated by CatalystAgile ScopeShield_
`;
}

export function exportProjectMarkdown(p: Project): string {
  const { extraHours, extraPrice } = approvedExtras(p);
  return `# ${p.name}

**Client:** ${p.client}
**Baseline date:** ${p.baselineDate}

## Quoted

- Hours: ${p.quotedHours}
- Price: $${p.quotedPrice.toLocaleString()}
- Rate: $${p.hourlyRate}/hr

## Locked deliverables (${p.deliverables.filter(d => d.locked).length})
${p.deliverables.filter(d => d.locked).map(d => `- **${d.title}** (${d.estimatedHours}h) — ${d.acceptanceCriteria}`).join('\n') || '_(none)_'}

## Approved change requests (${p.changes.filter(c => c.status === 'approved').length})
${p.changes.filter(c => c.status === 'approved').map(c => `- ${c.description} — +${c.estimatedHours}h / +$${c.priceImpact}`).join('\n') || '_(none)_'}

## Totals after approved CRs

- Hours: ${p.quotedHours + extraHours} (${scopeCreepPct(p).toFixed(1)}% creep)
- Price: $${(p.quotedPrice + extraPrice).toLocaleString()}

## Pending / rejected CRs
${p.changes.filter(c => c.status !== 'approved').map(c => `- [${c.status}] ${c.description}`).join('\n') || '_(none)_'}
`;
}
